import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Button,
    Stack,
    Typography,
} from '@mui/material';
import {
    Casino,
    DeleteOutline,
    ExpandMore,
    PersonAdd,
    Refresh,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../../../../shared/Settings/appSettings';
import { StyledIconButton } from '../../../../shared/Components/StyledIconButton';
import {
    formatDecimalFeetAsFeetInches,
    isFeetInchesNumberChallenge,
} from '@moment-golf/shared';
import type { EventScoreWithId, TbRegistration } from '@moment-golf/types';
import { useTBContext } from '../../../Contexts/useTBStates';
import { RoundButton } from '../../TabComponents';
import {
    type ChallengeGroupNode,
    type ChallengeGroupPathEntry,
    type EnrichedChallengeSubmission,
    getEnabledChallengeGroupDimensions,
    sortChallengeSubmissions,
} from '../../../utils/groupChallengeSubmissions';
import {
    buildChallengeGroupTreeFromRegistrations,
    removeChallengeSubmissionFromScore,
} from '../../../utils/challengeCommandCenterHelpers';
import {
    AddChallengeResultDialog,
    type ChallengeConfigRow,
} from './AddChallengeResultDialog';
import { ChallengeRandomDrawDialog } from './ChallengeRandomDrawDialog';

interface ChallengesPanelProps {
    appDims: any;
    compHeights: any;
}

interface AddDialogState {
    challenge: ChallengeConfigRow;
    groupPath?: ChallengeGroupPathEntry[];
}

interface SpinDialogState {
    challenge: ChallengeConfigRow;
    groupPath?: ChallengeGroupPathEntry[];
}

interface DeleteDialogState {
    submission: EnrichedChallengeSubmission;
}

export const ChallengesPanel = ({ compHeights }: ChallengesPanelProps) => {
    const api = CreateApi();
    const auth = useAuth();

    const {
        factor,
        appTheme,
        editMode,
        roundValue,
        setRoundValue,
        viewEvent,
        viewEventData,
        originalViewEventData,
        optionsLoaded,
    } = useTBContext();

    const [lastScoreQuery, setLastScoreQuery] = useState(new Date(Date.UTC(2000, 0, 1)));
    const [challengeScores, setChallengeScores] = useState<EnrichedChallengeSubmission[]>([]);
    const [addDialog, setAddDialog] = useState<AddDialogState | null>(null);
    const [spinDialog, setSpinDialog] = useState<SpinDialogState | null>(null);
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
    const [deleting, setDeleting] = useState(false);

    function getChallengeScores(scores?: EventScoreWithId[]) {
        const submissions: EnrichedChallengeSubmission[] = [];
        const scoresToMap = scores ?? viewEventData.scores;
        const regByPlayerName = new Map<string, TbRegistration>();

        viewEventData.registrations.forEach((reg) => {
            const name = reg.playerName?.trim();
            if (name) regByPlayerName.set(name, reg);
        });

        scoresToMap.forEach((score: EventScoreWithId) => {
            score.roundScores.forEach((roundScore, roundIndex) => {
                roundScore?.challengeSubmissions?.forEach((submission) => {
                    const reg = regByPlayerName.get(submission.playerName?.trim() ?? '');
                    submissions.push({
                        roundNumber: roundScore.roundNumber,
                        playerName: submission.playerName,
                        challengeName: submission.challengeName,
                        challengeHole: submission.challengeHole,
                        value: submission.value,
                        division: typeof reg?.division === 'string' ? reg.division.trim() : '',
                        flight: typeof reg?.flight === 'string' ? reg.flight.trim() : '',
                        gender: typeof reg?.gender === 'string' ? reg.gender.trim() : '',
                        age: typeof reg?.age === 'number' && Number.isFinite(reg.age) ? reg.age : null,
                        eventScoreID: String(score._id),
                        roundIndex,
                        submittedAt: submission.submittedAt ?? null,
                    });
                });
            });
        });

        return submissions;
    }

    const applyUpdatedScores = (updatedScores: EventScoreWithId[]) => {
        viewEventData.scores = JSON.parse(JSON.stringify(updatedScores));
        originalViewEventData.current.scores = JSON.parse(JSON.stringify(updatedScores));
        setChallengeScores(getChallengeScores(updatedScores));
    };

    const refreshScores = async () => {
        if (!viewEvent?._id) return;
        await api.get(`/tb/GetEventScores`, {
            params: {
                _id: viewEvent._id,
                referenceDate: lastScoreQuery,
            },
        })
            .then((res: { data: EventScoreWithId[] }) => {
                const updatedScores = JSON.parse(JSON.stringify(viewEventData.scores));
                updatedScores.forEach((score: EventScoreWithId, index: number) => {
                    const matchingScore = res.data.find((s) => s._id === score._id);

                    if (matchingScore) {
                        updatedScores[index] = matchingScore;
                    }
                });

                applyUpdatedScores(updatedScores);
            })
            .catch((err: any) => {
                console.error('Error refreshing scores', err);
            });
        setLastScoreQuery(new Date());
    };

    const confirmDelete = async () => {
        if (!deleteDialog) return;
        const { submission } = deleteDialog;
        const updatedScores: EventScoreWithId[] = JSON.parse(JSON.stringify(viewEventData.scores));
        const scoreIdx = updatedScores.findIndex(
            (s) => String(s._id) === String(submission.eventScoreID),
        );
        if (scoreIdx < 0) {
            setDeleteDialog(null);
            return;
        }

        const updatedChallenges = removeChallengeSubmissionFromScore(
            updatedScores[scoreIdx],
            submission.roundIndex,
            {
                challengeName: submission.challengeName,
                challengeHole: submission.challengeHole,
                playerName: submission.playerName,
                value: submission.value,
                submittedAt: submission.submittedAt,
            },
        );

        setDeleting(true);
        try {
            await api.post('/ls/UpdateChallengeScores', {
                eventScoreID: submission.eventScoreID,
                roundIndex: submission.roundIndex,
                challengeSubmissions: updatedChallenges,
                userID: auth.user?.profile.sub ?? 'commandCenter',
            });
            applyUpdatedScores(updatedScores);
            setDeleteDialog(null);
        } catch (err) {
            console.error('Error deleting challenge submission', err);
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        if (viewEventData?.scores?.length > 0) {
            setChallengeScores(getChallengeScores());
        }
    }, [roundValue, viewEventData.scores]);

    if (!viewEvent) {
        return null;
    }

    const roundChallenges = viewEvent?.rounds[roundValue - 1]?.challenges ?? [];

    const typeOrder: string[] = [];
    const seenType = new Set<string>();
    for (const c of roundChallenges) {
        const t = c?.type;
        if (t && !seenType.has(t)) {
            seenType.add(t);
            typeOrder.push(t);
        }
    }

    const challengesOpt = optionsLoaded.find((o: unknown) => (o as { keyName: string }).keyName === 'Challenges') as { options?: { type: string; dataModel?: { sortOrder?: unknown; inputType?: string; units?: string; decimalPlaces?: number } }[] } | undefined;

    const submissionMatchesConfigRow = (
        submission: { roundNumber: number; challengeName: string; challengeHole: number },
        row: { type?: string; name?: string; hole?: unknown },
    ) => {
        if (submission.roundNumber !== roundValue) return false;
        if (Number(submission.challengeHole) !== Number(row.hole)) return false;
        const label = row.name != null && String(row.name).trim() !== '' ? row.name : row.type;
        return submission.challengeName === label || submission.challengeName === row.type;
    };

    const challengeAccordionSx = {
        width: '98%',
        boxShadow: 'none',
        borderBottom: '1px solid #E0E0E0',
        backgroundColor: 'sepia.background',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': { backgroundColor: 'sepia.background' },
        '& .MuiAccordionDetails-root': { backgroundColor: 'sepia.background' },
    };

    const stopAccordionToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const renderActionIcons = (
        challengeRow: ChallengeConfigRow,
        opts: {
            groupPath?: ChallengeGroupPathEntry[];
            showSpin: boolean;
        },
    ) => (
        <Stack direction="row" spacing={0} alignItems="center" onClick={stopAccordionToggle}>
            <IconButton
                size="small"
                aria-label="Add challenge result"
                onClick={() =>
                    setAddDialog({
                        challenge: challengeRow,
                        groupPath: opts.groupPath,
                    })
                }
            >
                <PersonAdd fontSize="small" />
            </IconButton>
            {opts.showSpin ? (
                <IconButton
                    size="small"
                    aria-label="Random player draw"
                    onClick={() =>
                        setSpinDialog({
                            challenge: challengeRow,
                            groupPath: opts.groupPath,
                        })
                    }
                >
                    <Casino fontSize="small" />
                </IconButton>
            ) : null}
        </Stack>
    );

    const renderSubmissionRows = (
        submissions: EnrichedChallengeSubmission[],
        keyPrefix: string,
        challengeForFeet: { inputType?: string; units?: string },
        unitsLabel: string,
    ) =>
        submissions.map((submission, subIdx) => {
            const numVal = Number(submission.value);
            const showFeetInches =
                isFeetInchesNumberChallenge(challengeForFeet) && Number.isFinite(numVal);
            const displayValue: string | number | boolean = showFeetInches
                ? formatDecimalFeetAsFeetInches(numVal)
                : (submission.value as string | number | boolean);

            return (
                <Stack
                    key={`${keyPrefix}-${subIdx}-${submission.playerName}-${String(submission.submittedAt ?? '')}`}
                    direction='row'
                    spacing={Number(submission.value) > 1 ? 1 : 0.25}
                    alignItems='center'
                    justifyContent='center'
                    sx={{
                        width: '95%',
                        minWidth: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            flex: 3,
                            minWidth: 0,
                            textAlign: 'right',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {submission.playerName}
                    </Typography>
                    <Typography
                        sx={{
                            flex: Number(submission.value) > 1 ? 1 : 0,
                            textAlign: 'left',
                            fontWeight: 500,
                        }}
                    >
                        {displayValue}
                    </Typography>
                    <Typography
                        sx={{
                            flex: Number(submission.value) > 1 ? 2 : 4,
                            textAlign: 'left',
                            fontWeight: 500,
                        }}
                    >
                        {showFeetInches ? '' : unitsLabel}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label={`Delete challenge result for ${submission.playerName}`}
                        onClick={() => setDeleteDialog({ submission })}
                    >
                        <DeleteOutline fontSize="small" color="error" />
                    </IconButton>
                </Stack>
            );
        });

    const renderGroupNodes = (
        nodes: ChallengeGroupNode[],
        keyPrefix: string,
        challengeForFeet: ChallengeConfigRow,
        unitsLabel: string,
        sortOrder: unknown,
        challengeRow: ChallengeConfigRow,
        depth = 0,
    ): React.ReactNode =>
        nodes.map((node, idx) => {
            const nodeKey = `${keyPrefix}-group-${depth}-${node.label}-${idx}`;

            if (node.submissions) {
                const sorted = sortChallengeSubmissions(node.submissions, sortOrder);
                const empty = sorted.length === 0;
                return (
                    <Accordion
                        key={nodeKey}
                        defaultExpanded
                        disableGutters
                        square
                        sx={{
                            ...challengeAccordionSx,
                            width: depth === 0 ? '98%' : '100%',
                            pl: depth > 0 ? 1 : 0,
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore fontSize='small' />}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ width: '100%', pr: 1 }}
                            >
                                <Typography
                                    variant={depth === 0 ? 'subtitle2' : 'body2'}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {node.label}
                                </Typography>
                                {renderActionIcons(challengeRow, {
                                    groupPath: node.groupPath,
                                    showSpin: empty,
                                })}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Stack direction='column' spacing={0} alignItems='center' sx={{ width: '95%' }}>
                                {empty ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                                        No submissions
                                    </Typography>
                                ) : (
                                    renderSubmissionRows(sorted, nodeKey, challengeForFeet, unitsLabel)
                                )}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                );
            }

            if (!node.children?.length) return null;

            return (
                <Accordion
                    key={nodeKey}
                    defaultExpanded
                    disableGutters
                    square
                    sx={{
                        ...challengeAccordionSx,
                        width: depth === 0 ? '98%' : '100%',
                        pl: depth > 0 ? 1 : 0,
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMore fontSize='small' />}>
                        <Typography
                            variant={depth === 0 ? 'subtitle2' : 'body2'}
                            sx={{ fontWeight: 600 }}
                        >
                            {node.label}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Stack direction='column' spacing={0} alignItems='center' sx={{ width: '100%' }}>
                            {renderGroupNodes(
                                node.children,
                                nodeKey,
                                challengeForFeet,
                                unitsLabel,
                                sortOrder,
                                challengeRow,
                                depth + 1,
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            );
        });

    return (
        <Stack
            direction='column'
            spacing={0}
            alignItems='center'
            justifyContent='center'
            sx={{
                width: '100%',
                height: '100%',
            }}
        >
            <Stack
                direction='row'
                gap={'20px'}
                width={compHeights.appUsableWidth - 60}
                maxHeight={35}
                padding={'10px 40px'}
                alignItems='center'
                justifyContent='flex-end'
            >
                <Typography variant='h6' sx={{ flex: 1, textAlign: 'left', fontWeight: 500 }}>
                    Challenge Leaderboards
                </Typography>
                <StyledIconButton
                    appTheme={appTheme}
                    label='Refresh Scores'
                    factor={factor}
                    color='primary'
                    icon={<Refresh />}
                    width={'max-content'}
                    height={40}
                    onClick={refreshScores}
                />
                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
                    Round
                </Typography>
                <Stack
                    direction='row'
                    spacing={0}
                    padding={'0px 0px'}
                    alignItems='center'
                    justifyContent='flex-end'
                >
                    {Array.from({ length: Number(viewEvent?.totalRounds ?? 0) }, (_, index) => index + 1).map((number) => (
                        <RoundButton
                            key={`${roundValue}-${number}`}
                            number={number}
                            roundValue={roundValue}
                            editMode={editMode}
                            appTheme={appTheme}
                            factor={factor}
                            setRoundValue={setRoundValue}
                        />
                    ))}
                </Stack>
            </Stack>
            <Stack
                direction='row'
                gap={2}
                alignItems='flex-start'
                justifyContent='center'
                sx={{
                    width: '100%',
                    maxWidth: '90%',
                    alignSelf: 'center',
                    height: '90%',
                    padding: '0px 12px',
                    boxSizing: 'border-box',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    overflowX: 'hidden',
                }}
            >
                {typeOrder.map((challengeType) => {
                    const rowsForType = roundChallenges.filter((c: { type?: string }) => c.type === challengeType);
                    const holesUnique = Array.from(
                        new Set(
                            rowsForType
                                .map((r: { hole?: unknown }) => Number(r.hole))
                                .filter((h): h is number => Number.isFinite(h)),
                        ),
                    ).sort((a, b) => a - b);
                    const templateDm = challengesOpt?.options?.find((o) => o.type === challengeType)?.dataModel as
                        | { inputType?: string; units?: string; sortOrder?: unknown; decimalPlaces?: number }
                        | undefined;
                    const rep = rowsForType[0] as {
                        sortOrder?: unknown;
                        units?: string;
                        inputType?: string;
                        decimalPlaces?: number;
                    } | undefined;
                    const sortOrder = rep?.sortOrder ?? templateDm?.sortOrder;
                    const challengeForFeet = {
                        inputType: rep?.inputType ?? templateDm?.inputType,
                        units: rep?.units ?? templateDm?.units,
                    };
                    const unitsLabel = rep?.units ?? templateDm?.units ?? '';

                    return (
                        <Stack
                            key={challengeType}
                            direction='column'
                            spacing={1}
                            alignItems='center'
                            justifyContent='flex-start'
                            sx={{
                                flex: 1,
                                minWidth: 200,
                                height: '95%',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                border: '1px solid #E0E0E0',
                                borderRadius: 2,
                                padding: 1,
                                backgroundColor: 'sepia.background',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                            }}
                        >
                            <Typography
                                variant='h6'
                                sx={{
                                    fontWeight: 500,
                                    width: '100%',
                                    textAlign: 'center',
                                    px: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {challengeType}
                            </Typography>
                            <Divider flexItem />
                            <Stack
                                direction='column'
                                spacing={0}
                                alignItems='center'
                                justifyContent='flex-start'
                                sx={{
                                    width: '100%',
                                    minWidth: 200,
                                    flex: 1,
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    overflow: 'auto',
                                }}
                            >
                                {holesUnique.length === 0 ? (
                                    <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
                                        Set a hole number for this challenge
                                    </Typography>
                                ) : null}
                                {holesUnique.map((holeNum) => {
                                    const row = rowsForType.find((r: { hole?: unknown }) => Number(r.hole) === holeNum) as
                                        | ChallengeConfigRow & {
                                              byDivision?: boolean;
                                              byFlight?: boolean;
                                              byGender?: boolean;
                                              byAge?: boolean;
                                          }
                                        | undefined;
                                    if (!row) return null;

                                    const challengeRow: ChallengeConfigRow = {
                                        name: row.name,
                                        type: row.type ?? challengeType,
                                        hole: row.hole,
                                        inputType: row.inputType ?? templateDm?.inputType,
                                        units: row.units ?? templateDm?.units,
                                        decimalPlaces: row.decimalPlaces ?? templateDm?.decimalPlaces,
                                    };

                                    const filteredScores = challengeScores.filter((submission) =>
                                        submissionMatchesConfigRow(submission, row),
                                    );
                                    const enabledDimensions = getEnabledChallengeGroupDimensions(row);
                                    const groupNodes =
                                        enabledDimensions.length > 0
                                            ? buildChallengeGroupTreeFromRegistrations(
                                                  viewEventData.registrations,
                                                  filteredScores,
                                                  enabledDimensions,
                                                  viewEvent,
                                              )
                                            : [];
                                    const sortedFlatScores = sortChallengeSubmissions(filteredScores, sortOrder);
                                    const holeEmpty = filteredScores.length === 0;

                                    return (
                                        <Accordion
                                            key={`${challengeType}-${holeNum}`}
                                            defaultExpanded
                                            disableGutters
                                            square
                                            sx={challengeAccordionSx}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMore fontSize='small' />}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ width: '100%', pr: 1 }}
                                                >
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                                                        Hole {holeNum}
                                                    </Typography>
                                                    {renderActionIcons(challengeRow, {
                                                        showSpin: enabledDimensions.length === 0 && holeEmpty,
                                                    })}
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0, flex: 1 }}>
                                                <Stack
                                                    direction='column'
                                                    spacing={0}
                                                    alignItems='center'
                                                    justifyContent='flex-start'
                                                    sx={{ width: '95%' }}
                                                >
                                                    {enabledDimensions.length === 0
                                                        ? (
                                                            holeEmpty ? (
                                                                <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                                                                    No submissions
                                                                </Typography>
                                                            ) : (
                                                                renderSubmissionRows(
                                                                    sortedFlatScores,
                                                                    `${challengeType}-${holeNum}`,
                                                                    challengeForFeet,
                                                                    unitsLabel,
                                                                )
                                                            )
                                                        )
                                                        : renderGroupNodes(
                                                              groupNodes,
                                                              `${challengeType}-${holeNum}`,
                                                              challengeForFeet,
                                                              unitsLabel,
                                                              sortOrder,
                                                              challengeRow,
                                                          )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>

            {addDialog ? (
                <AddChallengeResultDialog
                    open
                    onClose={() => setAddDialog(null)}
                    challenge={addDialog.challenge}
                    roundNumber={roundValue}
                    registrations={viewEventData.registrations}
                    scores={viewEventData.scores}
                    groupPath={addDialog.groupPath}
                    onSaved={applyUpdatedScores}
                />
            ) : null}

            {spinDialog ? (
                <ChallengeRandomDrawDialog
                    open
                    onClose={() => setSpinDialog(null)}
                    challenge={spinDialog.challenge}
                    roundNumber={roundValue}
                    registrations={viewEventData.registrations}
                    scores={viewEventData.scores}
                    groupPath={spinDialog.groupPath}
                    onSaved={applyUpdatedScores}
                />
            ) : null}

            <Dialog
                open={Boolean(deleteDialog)}
                onClose={() => (deleting ? undefined : setDeleteDialog(null))}
            >
                <DialogTitle>Remove challenge result?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Remove challenge result for{' '}
                        <strong>{deleteDialog?.submission.playerName}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
